import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { heroData, companyInfo } from "../data/mock";
import {
  ShieldCheck,
  Building2,
  Timer,
  ArrowRight,
  MessageCircle,
} from "lucide-react";

const iconMap = {
  ShieldCheck,
  Building2,
  Timer,
};

export const HeroSection = () => {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background Image with overlay */}
      <div className="absolute inset-0">
        <img
          src={heroData.heroImage}
          alt="Industrial scrap management"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-teal-500/8 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 w-full">
        <div className="max-w-3xl">
          {/* Trust tag */}
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border border-emerald-400/20 rounded-full px-4 py-2 mb-8 animate-fade-in">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">
              Government Authorized Scrap Dealer
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4 tracking-tight">
            {heroData.headline}
            <br />
            <span className="text-emerald-400 mt-2 block">
              {heroData.headlineAccent}
            </span>
          </h1>

          {/* Subtext */}
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl">
            {heroData.subtext}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-14">
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl px-8 py-6 text-base font-semibold shadow-xl shadow-emerald-900/30 hover:shadow-emerald-800/40 transition-all duration-300 hover:-translate-y-0.5"
              onClick={() => scrollTo("#contact")}
            >
              Get Quote
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-xl px-8 py-6 text-base font-semibold backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5"
              onClick={() => scrollTo("#how-it-works")}
            >
              Schedule Pickup
            </Button>
            <a
              href={`https://wa.me/${companyInfo.whatsapp}?text=Hi%20AM%20Scrap%20Dealers%2C%20I%20would%20like%20to%20enquire%20about%20scrap%20pickup.`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl px-8 py-6 text-base font-semibold shadow-xl shadow-green-900/20 transition-all duration-300 hover:-translate-y-0.5"
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                WhatsApp Enquiry
              </Button>
            </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-4">
            {heroData.trustBadges.map((badge, i) => {
              const Icon = iconMap[badge.icon];
              return (
                <Badge
                  key={i}
                  className="bg-white/10 backdrop-blur-md border-white/10 text-white hover:bg-white/15 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors duration-200"
                >
                  {Icon && <Icon className="w-4 h-4 text-emerald-400" />}
                  {badge.label}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};
